import contente1 from '@/assets/p2p_landing/contente_1.jpg';
import relaxado1 from '@/assets/p2p_landing/relaxado_1.jpg';
import reuniao1 from '@/assets/p2p_landing/reuniao_1.jpg';
import reuniao2 from '@/assets/p2p_landing/reuniao_2.jpg';
import dailyLogo from '@/assets/partners/daily-logo.svg';
import digitalOceanLogo from '@/assets/partners/digitalocean.svg';
import googleCalendarLogo from '@/assets/partners/google-calendar.png';
import googleCloudLogo from '@/assets/partners/google-cloud.png';
import stripeLogo from '@/assets/partners/stripe.webp';
import vercelLogo from '@/assets/partners/vercel.webp';
import {
	Cloud,
	FileStack,
	Headphones,
	Layers,
} from 'lucide-react';

export const formatPsychologistPrice = (value: number) =>
	value.toFixed(2).replace('.', ',');

export type PlanFeature = {
	text: string;
	highlight?: boolean;
};

export const P2P_PLAN = {
	id: 'p2p',
	name: 'Plumaa para psicólogos',
	price: 99,
	firstMonthPrice: 89,
	trialDays: 7,
	features: [
		{
			text: 'Evolução automática de prontuário com IA',
			highlight: true,
		},
		{ text: 'Prontuário evoluído para sessões presenciais' },
		{ text: 'Lembrete automático por WhatsApp' },
		{ text: 'Videoconferência integrada no navegador' },
		{ text: 'Agenda online com sincronização Google Calendar' },
		{ text: 'Armazenamento em nuvem de 1 TB' },
		{ text: 'Suporte humano especializado' },
	] satisfies PlanFeature[],
};

export type LandingSolution = {
	id: string;
	title: string;
	description: string;
	image: string;
};

export const LANDING_SOLUTIONS: LandingSolution[] = [
	{
		id: 'burocracia',
		title: 'Prontuário evoluído por IA',
		description:
			'A Plumaa transcreve e preenche a evolução do prontuário automaticamente após cada sessão. Você foca no paciente.',
		image: contente1,
	},
	{
		id: 'faltas',
		title: 'Lembretes automáticos por WhatsApp',
		description:
			'Reduza faltas com mensagens automáticas antes de cada consulta. Sua agenda fica organizada sem esforço extra.',
		image: relaxado1,
	},
	{
		id: 'apps',
		title: 'Agenda, vídeo e prontuário integrados',
		description:
			'Pare de alternar entre sites e apps. A Plumaa reúne agenda, videoconferência, prontuário e lembretes em uma única plataforma.',
		image: reuniao2,
	},
	{
		id: 'papel',
		title: 'Prontuário digital na nuvem',
		description:
			'Tudo fica digital, seguro e acessível em segundos — com conformidade LGPD e evolução automática gerada por IA.',
		image: reuniao1,
	},
];

export type WorkflowDemo = {
	id: string;
	eyebrow: string;
	title: string;
	titleAccent: string;
	description: string;
	tag: string;
	src: string | null;
	alt: string;
	mediaFirst?: boolean;
};

export const WORKFLOW_DEMOS: WorkflowDemo[] = [
	{
		id: 'agendamentos',
		eyebrow: 'Agenda e lembretes',
		title: 'Agendamentos com seus pacientes',
		titleAccent: 'e notificação via WhatsApp.',
		description:
			'Cadastre pacientes, defina horários e sincronize com o Google Calendar. A Plumaa envia lembretes automáticos por WhatsApp antes de cada sessão.',
		tag: 'Google Calendar · WhatsApp automático',
		src: null,
		alt: 'Demonstração de agendamentos com notificação via WhatsApp',
	},
	{
		id: 'chamada',
		eyebrow: 'Atendimento online',
		title: 'Videochamada',
		titleAccent: '+ prontuário.',
		description:
			'Conduza a sessão por videoconferência direto no navegador. O prontuário fica disponível ao lado da chamada, em tempo real.',
		tag: 'Sem instalar apps · sigilo garantido',
		src: null,
		alt: 'Demonstração de videochamada com prontuário',
		mediaFirst: true,
	},
	{
		id: 'evolucao',
		eyebrow: 'Inteligência artificial',
		title: 'Evolução automática',
		titleAccent: 'após a sessão.',
		description:
			'A IA da Plumaa transcreve o atendimento com segurança e confidencialidade, preparando o registro clínico enquanto você conduz a sessão.',
		tag: 'Transcrição segura · padrões éticos CFP',
		src: null,
		alt: 'Demonstração de evolução automática com IA',
	},
	{
		id: 'prontuario',
		eyebrow: 'Documentação clínica',
		title: 'Prontuário gerado',
		titleAccent: 'sem burocracia.',
		description:
			'Prontuário com dados estruturados e adaptado à sua abordagem terapêutica, com anamnese sempre à mão. Menos tempo em papelada, mais tempo na clínica.',
		tag: 'Online e presencial · revisão antes de salvar',
		src: null,
		alt: 'Demonstração de prontuário gerado automaticamente',
		mediaFirst: true,
	},
];

export type ComparisonRow = {
	feature: string;
	others: string;
	plumaa: string;
	plumaaHighlight?: boolean;
};

export const COMPARISON_ROWS: ComparisonRow[] = [
	{
		feature: 'Preço mensal',
		others: 'R$ 120 – R$ 180+',
		plumaa: 'R$ 99',
		plumaaHighlight: true,
	},
	{
		feature: 'Evolução automática com IA',
		others: 'Parcial ou pago à parte',
		plumaa: 'Incluso',
		plumaaHighlight: true,
	},
	{
		feature: 'Prontuário para sessões presenciais',
		others: 'Raro',
		plumaa: 'Incluso',
		plumaaHighlight: true,
	},
	{
		feature: 'Videoconferência integrada',
		others: 'Às vezes',
		plumaa: 'Incluso',
		plumaaHighlight: true,
	},
	{
		feature: 'Google Calendar',
		others: 'Às vezes',
		plumaa: 'Incluso',
		plumaaHighlight: true,
	},
	{
		feature: 'Lembrete WhatsApp',
		others: 'Extra',
		plumaa: 'Incluso',
		plumaaHighlight: true,
	},
	{
		feature: 'Armazenamento',
		others: 'Limitado',
		plumaa: '1 TB',
		plumaaHighlight: true,
	},
	{
		feature: 'Suporte',
		others: 'Chat genérico',
		plumaa: 'Humano especializado',
		plumaaHighlight: true,
	},
];

export type FaqItem = {
	id: string;
	question: string;
	answer: string;
};

export const FAQ_ITEMS: FaqItem[] = [
	{
		id: 'o-que-e',
		question: 'O que é a Plumaa para psicólogos?',
		answer: 'É o consultório digital da Plumaa para psicólogos que atendem seus próprios pacientes. Você agenda, realiza videoconferências, envia lembretes por WhatsApp e documenta sessões com evolução automática de prontuário por IA — tudo em uma única plataforma.',
	},
	{
		id: 'cartao',
		question: 'Preciso de cartão para o teste de 7 dias?',
		answer: 'Não. Basta criar sua conta e começar a usar. Após os 7 dias grátis, o primeiro mês custa R$ 89 e, a partir do segundo mês, R$ 99/mês.',
	},
	{
		id: 'presencial',
		question: 'Posso usar em sessões presenciais?',
		answer: 'Sim. A Plumaa para psicólogos também gera evolução de prontuário para sessões presenciais. Basta iniciar a transcrição durante o atendimento e a IA documenta o registro clínico ao final.',
	},
	{
		id: 'ia-grava',
		question: 'A IA grava ou transcreve a sessão?',
		answer: 'Sim. Durante o atendimento, a IA transcreve o conteúdo com segurança e confidencialidade. Ao encerrar a sessão, gera automaticamente a evolução do prontuário, respeitando os padrões éticos da Psicologia.',
	},
	{
		id: 'google-calendar',
		question: 'Funciona com Google Calendar?',
		answer: 'Sim. A Plumaa sincroniza sua agenda com o Google Calendar, cria links de videochamada automaticamente e envia convites aos pacientes.',
	},
	{
		id: 'cancelar',
		question: 'Posso cancelar quando quiser?',
		answer: 'Sim. Não há fidelidade nem multa de permanência. Você pode cancelar a qualquer momento diretamente na plataforma.',
	},
	{
		id: 'lgpd',
		question: 'É seguro e sigiloso (LGPD)?',
		answer: 'Sim. A Plumaa segue a LGPD e as resoluções do CFP sobre prontuário digital. Seus dados e os de seus pacientes ficam armazenados com criptografia em nuvem.',
	},
];

export const TRUST_ITEMS = [
	{
		id: 'lgpd',
		title: 'Conformidade LGPD',
		Icon: FileStack,
		accent: 'orange' as const,
	},
	{
		id: 'cfp',
		title: 'Padrões CFP',
		Icon: Layers,
		accent: 'pink' as const,
	},
	{
		id: 'cloud',
		title: 'Nuvem segura',
		Icon: Cloud,
		accent: 'blue' as const,
	},
	{
		id: 'suporte',
		title: 'Suporte dedicado',
		Icon: Headphones,
		accent: 'orange' as const,
	},
];

export type PartnerIntegration = {
	name: string;
	logo: string;
	logoScale?: number;
};

export const PARTNER_INTEGRATIONS: PartnerIntegration[] = [
	{ name: 'DigitalOcean', logo: digitalOceanLogo },
	{ name: 'Stripe', logo: stripeLogo },
	{ name: 'Daily.co', logo: dailyLogo },
	{ name: 'Vercel', logo: vercelLogo, logoScale: 0.78 },
	{ name: 'Google Calendar', logo: googleCalendarLogo, logoScale: 1.45 },
	{ name: 'Google Cloud', logo: googleCloudLogo, logoScale: 1.45 },
];
